import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import serviceWebsites from "@/assets/service-websites.webp";
import serviceSeo from "@/assets/service-seo.webp";
import serviceAds from "@/assets/service-ads.webp";
import serviceMail from "@/assets/service-mail.webp";

const services = [
  {
    image: serviceWebsites,
    title: "Custom Websites",
    desc: "Professional, high-performing dental websites built to convert visitors into booked appointments.",
    link: "https://www.xpresspromotion.com/services/websites",
    cta: "Get a High-Converting Site",
  },
  {
    image: serviceSeo,
    title: "SEO",
    desc: "Local and organic SEO so patients in your area find you first when they search for a dentist.",
    link: "https://www.xpresspromotion.com/services/seo",
    cta: "Start My SEO",
  },
  {
    image: serviceAds,
    title: "Paid Ads",
    desc: "Google and social ads focused on qualified patient leads with measurable, trackable results.",
    link: "https://www.xpresspromotion.com/services/paid-ads",
    cta: "Start Running Ads",
  },
  {
    image: serviceMail,
    title: "Printing & Mailing",
    desc: "Direct mail campaigns — new mover postcards, promos, and outreach that fill your schedule.",
    link: "https://www.xpresspromotion.com/services/printing-mailing",
    cta: "Launch a Mail Campaign",
  },
];

const ServicesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-background" ref={ref}>
      <div className="max-w-7xl mx-auto section-padding">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-3">
            Our Core Marketing Services
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
            Everything You Need to Attract & Convert Patients
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div
              key={s.title}
              className={`group bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl hover:shadow-foreground/5 transition-all duration-300 flex flex-col ${
                visible ? "reveal-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">{s.desc}</p>
                <a
                  href={s.link}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:gap-3 transition-all duration-200"
                >
                  {s.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
